import { useRef, useImperativeHandle, useEffect, useState } from 'react'
import { actingAs, testEmail } from '@/lib/testActAs'
import { useStytch, useStytchUser } from '@stytch/react'
import { useQueryClient } from '@tanstack/react-query'
import { accountChanged } from './accountChange'
import AuthContext from './AuthContext'
import appConfig from '@/configs/app.config'
import { useSessionUser } from '@/store/authStore'
import { REDIRECT_URL_KEY } from '@/constants/app.constant'
import { useNavigate } from 'react-router'
import type {
    SignInCredential,
    SignUpCredential,
    AuthResult,
    OauthSignInCallbackPayload,
    Token,
    User,
} from '@/@types/auth'
import type { ReactNode, Ref } from 'react'
import type { NavigateFunction } from 'react-router'

type AuthProviderProps = { children: ReactNode }

export type IsolatedNavigatorRef = {
    navigate: NavigateFunction
}

const IsolatedNavigator = ({ ref }: { ref: Ref<IsolatedNavigatorRef> }) => {
    const navigate = useNavigate()

    useImperativeHandle(ref, () => {
        return {
            navigate,
        }
    }, [navigate])

    return <></>
}

/**
 * Auth state lives in the Stytch SDK. We adapt their hook shape into
 * the legacy `{ user, authenticated, signIn, signOut, ... }` contract
 * that the rest of the app reads from `useAuth()`.
 *
 * Compared to the previous Kinde version, Stytch handles session
 * refresh transparently — no need for the "settled-state" gymnastics
 * that papered over Kinde's loading-flag thrash during silent
 * refreshes. The SDK exposes a stable `isInitialized` boolean that we
 * just thread through.
 */
function AuthProvider({ children }: AuthProviderProps) {
    // E2E mode auto-authenticates every test so they don't have to
    // round-trip through Stytch. The auth-signup spec (which IS the
    // round-trip) opts out by setting this sessionStorage key in
    // addInitScript BEFORE the page boots; we read it once at mount
    // so the bypass behaves consistently for the page's lifetime.
    const isE2E =
        !!import.meta.env.VITE_E2E &&
        !(typeof window !== 'undefined' &&
            window.sessionStorage?.getItem('__lilia_disable_e2e_auth') === '1')
    const stytch = useStytch()
    const { user: stytchUser, isInitialized } = useStytchUser()

    const user = useSessionUser((state) => state.user)
    const setUser = useSessionUser((state) => state.setUser)
    const setSessionSignedIn = useSessionUser(
        (state) => state.setSessionSignedIn,
    )

    const navigatorRef = useRef<IsolatedNavigatorRef>(null)
    // The cached server data is the signed-in account's: forget it when the account changes (accountChange.ts).
    const queryClient = useQueryClient()
    const accountRef = useRef<string | null | undefined>(undefined)

    // Once isInitialized flips true, the SDK has resolved whether
    // there's a session (from cookies). We track "ever-settled" so
    // subsequent silent re-reads don't cause unmounts — same defensive
    // pattern we used with Kinde, kept in case Stytch ever flickers.
    const [hasSettled, setHasSettled] = useState(false)
    useEffect(() => {
        if (isInitialized) setHasSettled(true)
    }, [isInitialized])

    // signOut() flips this to true so `authenticated` evaluates to
    // false immediately, without waiting for stytch.session.revoke()
    // to propagate back to the useStytchUser hook. Without it, the
    // first signOut click hits a window where stytchUser is still set,
    // PublicRoute on /sign-in sees authenticated=true, and bounces the
    // user back to the dashboard — hence the reported "many tries to
    // sign out". Resets when stytchUser actually flips to null (or on
    // any re-sign-in).
    const [signingOut, setSigningOut] = useState(false)

    // Sync Stytch auth state into session store
    useEffect(() => {
        if (isE2E) {
            setSessionSignedIn(true)
            // The test copy's "Sign in as…" (lib/testActAs): the person chosen, or the owner.
            const person = actingAs()
            setUser(
                person
                    ? { avatar: '', userName: person.name, email: testEmail(person.id), authority: ['user'], userId: person.id }
                    : { avatar: '', userName: 'E2E Test User', email: 'e2e@test.local', authority: ['user'], userId: 'e2e-test-user' },
            )
            return
        }

        if (!isInitialized) return

        const account = stytchUser?.user_id ?? null
        if (accountChanged(accountRef.current, account)) queryClient.clear()
        accountRef.current = account

        if (stytchUser) {
            const primaryEmail =
                stytchUser.emails?.find((e) => e.verified)?.email ??
                stytchUser.emails?.[0]?.email ??
                ''
            const firstName = stytchUser.name?.first_name ?? ''
            const lastName = stytchUser.name?.last_name ?? ''
            // An email-and-password sign-up keeps its name in the metadata (SignUp.tsx), not in `name`.
            const typedName = (stytchUser.trusted_metadata as { display_name?: unknown } | undefined)?.display_name
            const displayName =
                firstName || lastName
                    ? `${firstName} ${lastName}`.trim()
                    : typeof typedName === 'string' && typedName.trim()
                      ? typedName.trim()
                      : primaryEmail

            setSessionSignedIn(true)
            setUser({
                avatar: '',
                userName: displayName,
                email: primaryEmail,
                authority: ['user'],
                userId: stytchUser.user_id,
            })
            // Stytch surfaced a user — any in-flight sign-out is done.
            setSigningOut(false)
        } else {
            setSessionSignedIn(false)
            setUser({})
            // stytchUser is null → revoke() has propagated, clear the
            // pessimistic override so a subsequent sign-in works.
            setSigningOut(false)
        }
    }, [stytchUser, isInitialized, setSessionSignedIn, setUser, isE2E, queryClient])

    const authenticated = Boolean(
        !signingOut &&
            (isE2E ||
                (hasSettled && !!stytchUser) ||
                (!hasSettled && !!stytchUser && isInitialized)),
    )

    const redirect = () => {
        const search = window.location.search
        const params = new URLSearchParams(search)
        const redirectUrl = params.get(REDIRECT_URL_KEY)

        navigatorRef.current?.navigate(
            redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath,
        )
    }

    const signIn = async (_values: SignInCredential): AuthResult => {
        // Actual auth happens inside the Stytch SDK on the /sign-in
        // page. This callback just redirects after the SDK has set
        // the session cookie. Kept for backwards-compat with consumers
        // of the AuthContext that call signIn().
        redirect()
        return { status: 'success', message: '' }
    }

    const signUp = async (_values: SignUpCredential): AuthResult => {
        redirect()
        return { status: 'success', message: '' }
    }

    const signOut = async () => {
        // Flip the override BEFORE clearing local state so the next
        // render sees authenticated=false immediately — otherwise
        // PublicRoute on /sign-in would see the still-set stytchUser
        // and bounce us back to /dashboard. See the signingOut state
        // comment above.
        setSigningOut(true)
        setSessionSignedIn(false)
        setUser({})
        // Nothing of this account stays on screen or in memory for the next one.
        queryClient.clear()
        try {
            await stytch.session.revoke()
        } catch {
            // Session may already be revoked or expired — proceed
            // with local sign-out either way.
        }
        // Send the user back to the public landing. signingOut keeps
        // authenticated=false until stytchUser propagates null.
        navigatorRef.current?.navigate('/sign-in')
    }

    const oAuthSignIn = (
        callback: (payload: OauthSignInCallbackPayload) => void,
    ) => {
        callback({
            onSignIn: (_tokens: Token, _user?: User) => {},
            redirect,
        })
    }

    return (
        <AuthContext.Provider
            value={{
                authenticated,
                // Stay in `loading` until the SDK has settled (hasSettled lags
                // isInitialized by one render, giving stytchUser time to
                // populate). Using `!isInitialized` here flipped loading→false
                // a render too early, so a hard-loaded protected route briefly
                // saw authenticated=false and bounced through /sign-in to the
                // entry path (dropping redirectUrl). hasSettled stays true
                // across silent refreshes, so this doesn't re-flash.
                loading: !hasSettled,
                user,
                signIn,
                signUp,
                signOut,
                oAuthSignIn,
            }}
        >
            {children}
            <IsolatedNavigator ref={navigatorRef} />
        </AuthContext.Provider>
    )
}

export default AuthProvider
